import { CloseButton } from '@/lib/base/buttons/close-button';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { cx } from '@/utils/cx';

const LIVE_LABEL = 'Working on it…';
const SETTLED_LABEL = 'Done';

/**
 * Animated "live" dot — ping halo over a solid core. Decorative, hidden from
 * a11y tree (the surrounding user message + content carry the meaning).
 */
function LiveDot() {
  return (
    <span
      aria-hidden='true'
      className='relative flex h-2 w-2 shrink-0'
      data-component='live-indicator'
      data-state='live'
      data-testid='agent-response-live-dot'
    >
      <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-utility-brand-400 opacity-75' />
      <span className='relative inline-flex h-2 w-2 rounded-full bg-utility-brand-500' />
    </span>
  );
}

interface ChatResponsePanelProps {
  /** Markdown content from the agent's latest reasoning step. */
  content?: string;
  /** The user message that prompted this response — rendered as a small,
   * muted snippet so it's clear what the agent is responding to. */
  userMessage?: string;
  /** True while the agent is actively running. While live the close button
   * is hidden (no dismissal mid-thought), a "Working on it…" header is
   * shown with a live dot, and content is demoted to a smaller, muted
   * weight. Once settled, the header reads "Done" and content returns to
   * full weight. */
  isLive?: boolean;
  /** User dismissed the settled panel — hide until the next chain starts.
   * Only invoked when `isLive` is false (close button is hidden otherwise). */
  onDismiss: () => void;
  /** Org ID forwarded to MarkdownContent for entity-link resolution. */
  orgId?: number;
}

/**
 * Shared presentational panel for displaying an agent's response to the user
 * during a chat interaction.
 *
 * Layout (intentionally minimal):
 *  - Status header: "Working on it…" (with live dot) while live, "Done"
 *    once settled with content.
 *  - Optional user-message quote.
 *  - Optional markdown content from the latest reasoning step, demoted to
 *    a smaller, muted weight while live and full weight once settled.
 *  - Close button when settled.
 */
export function ChatResponsePanel({
  content,
  userMessage,
  isLive = false,
  onDismiss,
  orgId,
}: ChatResponsePanelProps) {
  const trimmedContent = content?.trim();
  const trimmedUserMessage = userMessage?.trim();
  if (!isLive && !trimmedContent && !trimmedUserMessage) {
    return null;
  }

  let statusLabel: string | null = null;
  if (isLive) {
    statusLabel = LIVE_LABEL;
  } else if (trimmedContent) {
    statusLabel = SETTLED_LABEL;
  }

  return (
    <div
      role='status'
      aria-live='polite'
      aria-atomic='false'
      className='border-t border-border-secondary bg-bg-primary px-4 py-3 animate-in fade-in slide-in-from-bottom-2 duration-200 ease-out'
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='flex-1 min-w-0'>
          {(isLive || statusLabel || trimmedUserMessage) && (
            <div
              className={`flex items-center gap-2 ${trimmedContent ? 'mb-[9px]' : ''}`}
            >
              {isLive && <LiveDot />}
              {statusLabel && (
                <span
                  className='text-xs font-medium text-fg-tertiary'
                  data-testid='agent-response-status'
                >
                  {statusLabel}
                </span>
              )}
              {trimmedUserMessage && (
                <p
                  className='text-xs text-fg-tertiary line-clamp-2 italic min-w-0'
                  data-testid='agent-response-user-message'
                >
                  {trimmedUserMessage}
                </p>
              )}
            </div>
          )}

          {trimmedContent && (
            <div
              className={cx(
                isLive ? 'text-xs text-fg-tertiary' : 'text-sm text-fg-primary'
              )}
            >
              <MarkdownContent
                content={trimmedContent}
                size='compact'
                orgId={orgId}
              />
            </div>
          )}
        </div>
        {!isLive && (
          <CloseButton
            onPress={onDismiss}
            theme='light'
            size='xs'
            label='Dismiss agent response'
          />
        )}
      </div>
    </div>
  );
}
